/**
 * BCP sync-job summary utilities.
 *
 * TypeScript translations of the Python helpers under
 * `customer-sync-zip/agent-prompt`:
 *
 *  - `aggregate_flow_errors.py` -> {@link aggregateFlowErrorsFromZip}
 *  - `extract_tf_errors.py`     -> {@link extractTerraformErrorEntries}
 *  - `minify_tf_errors.py`      -> {@link minifyTerraformErrorsByResourceType}
 *
 * The minified JSON produced here is the same shape the Python pipeline
 * writes to `flow-errors-minified.json` / `2nd-import-errors-minified.json`,
 * so the agent prompt can include it verbatim and the user can also
 * download it from the chat UI for offline inspection.
 */
import JSZip from 'jszip';
import type { TransactionDetail, JobDetailsResponse } from '@/common-types';
import { transactionService, jobService } from '@/services/api';

// ───────────────────────────────────────────────────────────────────────────────
// Terraform errors  (mirrors `minify_tf_errors.py`)
// ───────────────────────────────────────────────────────────────────────────────

export interface TerraformErrorEntry {
  '@level'?: string;
  '@message'?: string;
  '@module'?: string;
  '@timestamp'?: string;
  type?: string;
  diagnostic?: {
    severity?: string;
    summary?: string;
    detail?: string;
    address?: string;
  };
  [key: string]: unknown;
}

export interface TerraformErrorsResponse {
  errors?: TerraformErrorEntry[];
  totalLogs?: number;
  totalErrors?: number;
  processedAt?: string;
  processingError?: string;
}

export interface MinifiedTerraformResource {
  message: string;
  detail?: string;
  resourceName: string;
}

export interface MinifiedTerraformGroup {
  resourceType: string;
  count: number;
  resources: MinifiedTerraformResource[];
}

/**
 * Mirror of `extract_tf_errors.py`: keep only entries with `@level === 'error'`.
 * The backend's `terraformErrors.presignedUrl` already returns a pre-filtered
 * `{ errors: [...] }` payload, but raw stdout-style arrays are also accepted
 * so this works against either source.
 */
export function extractTerraformErrorEntries(
  data: TerraformErrorsResponse | TerraformErrorEntry[] | unknown,
): TerraformErrorEntry[] {
  if (!data) return [];
  if (Array.isArray(data)) {
    return (data as TerraformErrorEntry[]).filter(
      (e) => (e?.['@level'] ?? '').toLowerCase() === 'error',
    );
  }
  if (typeof data === 'object') {
    const resp = data as TerraformErrorsResponse;
    if (resp.processingError) return [];
    return resp.errors ?? [];
  }
  return [];
}

/**
 * Mirror of `minify_tf_errors.py`: group errors by `diagnostic.address`
 * resource type prefix, producing
 * `[{ resourceType, count, resources: [{ message, detail, resourceName }] }]`.
 */
export function minifyTerraformErrorsByResourceType(
  data: TerraformErrorsResponse | TerraformErrorEntry[] | unknown,
): MinifiedTerraformGroup[] {
  const errors = extractTerraformErrorEntries(data);
  const grouped = new Map<string, MinifiedTerraformResource[]>();

  for (const item of errors) {
    const address = item.diagnostic?.address ?? '';
    const dot = address.indexOf('.');
    const resourceType = (dot >= 0 ? address.slice(0, dot) : address) || 'unknown';
    const resourceName = dot >= 0 ? address.slice(dot + 1) : '';

    const list = grouped.get(resourceType) ?? [];
    list.push({
      message: typeof item['@message'] === 'string' ? item['@message'] : '',
      detail: item.diagnostic?.detail,
      resourceName,
    });
    grouped.set(resourceType, list);
  }

  return Array.from(grouped.entries()).map(([resourceType, resources]) => ({
    resourceType,
    count: resources.length,
    resources,
  }));
}

// ───────────────────────────────────────────────────────────────────────────────
// Flow errors  (mirrors `aggregate_flow_errors.py`)
// ───────────────────────────────────────────────────────────────────────────────

export interface MinifiedFlowEntry {
  flowName: string;
  errors: string[];
}

export type MinifiedFlowErrors = Record<string, MinifiedFlowEntry[]>;

const FLOW_PUBLISH_RESULT_PATTERN = /-publish-results-\d+\.json$/i;

const FLOW_NOISE_PATTERNS: RegExp[] = [
  /^setting the Archy exit code to/i,
  /^error\(s\) encountered\.?$/i,
  /^exit code:/i,
  /^An error occurred\.?$/i,
  /^___ Yaml Info ___/,
  /^ref path:/,
  /^name: /,
  /^Architect Scripting session ended in error/i,
  /^Error\(s\) and warning\(s\) encountered\.?$/i,
  /^ERROR - the flow has \d+ error/i,
  /^- ERROR! ending the Session/i,
  /^- ERROR! session startup/i,
  /^- ERROR! promise unhandled exception caught/i,
  /^- ERROR! ArchSession\.startWithClientIdAndSecret/i,
  /^- Architect Scripting errors/i,
  /^not publishing the flow/i,
  /^failed to publish the flow/i,
  /^\d+ -> /,
  /^There is one action in error within this task$/,
  /^There is one action with a warning within this task$/,
];

function isFlowNoise(text: string | undefined): boolean {
  const t = (text ?? '').trim();
  if (!t) return true;
  return FLOW_NOISE_PATTERNS.some((p) => p.test(t));
}

interface FlowTrace {
  type?: string;
  text?: string;
}

/** Mirror of `collect_errors` in `aggregate_flow_errors.py`. */
function collectMeaningfulFlowErrors(traces: FlowTrace[] | undefined): string[] {
  const errors = (traces ?? [])
    .filter((tr) => tr?.type === 'error')
    .map((tr) => (tr?.text ?? '').toString());

  const out: string[] = [];
  const seen = new Set<string>();

  for (const raw of errors) {
    if (isFlowNoise(raw)) continue;
    const t = raw.trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }

  if (out.length === 0) {
    for (const raw of errors) {
      const t = (raw ?? '').trim();
      if (!t || seen.has(t)) continue;
      seen.add(t);
      out.push(t);
    }
  }

  return out;
}

interface ArchyPublishResultJson {
  output?: {
    flowName?: string;
    traces?: FlowTrace[];
  };
}

/**
 * Mirror of `aggregate_flow_errors.run`: walks every
 * `*-publish-results-N.json` entry inside the publishedFlows-errors zip,
 * extracts meaningful errors, and groups them by parent folder (module)
 * exactly like the Python script.
 */
export async function aggregateFlowErrorsFromZip(blob: Blob): Promise<MinifiedFlowErrors> {
  const zip = await new JSZip().loadAsync(blob);
  const buckets = new Map<string, MinifiedFlowEntry[]>();

  const entries = Object.values(zip.files).filter(
    (f) => !f.dir && FLOW_PUBLISH_RESULT_PATTERN.test(f.name),
  );

  for (const entry of entries) {
    let json: ArchyPublishResultJson;
    try {
      json = JSON.parse(await entry.async('string')) as ArchyPublishResultJson;
    } catch {
      continue;
    }

    const segments = entry.name.split('/').filter(Boolean);
    const fileName = segments[segments.length - 1] ?? entry.name;
    const moduleName = segments.length > 1 ? segments[segments.length - 2] : 'root';
    const flowName =
      json.output?.flowName ?? fileName.replace(FLOW_PUBLISH_RESULT_PATTERN, '');

    let errs = collectMeaningfulFlowErrors(json.output?.traces);
    if (errs.length === 0) errs = ['NO_ERROR_EXTRACTED'];

    const list = buckets.get(moduleName) ?? [];
    list.push({ flowName, errors: errs });
    buckets.set(moduleName, list);
  }

  for (const list of buckets.values()) {
    list.sort((a, b) => a.flowName.localeCompare(b.flowName));
  }

  return Object.fromEntries(
    Array.from(buckets.entries()).sort(([a], [b]) => a.localeCompare(b)),
  );
}

// ───────────────────────────────────────────────────────────────────────────────
// Fetch helpers
// ───────────────────────────────────────────────────────────────────────────────

export interface TerraformErrorsFetchResult {
  /** Source URL the entries were extracted from (for diagnostics / downloads). */
  source: 'terraformLogs' | 'terraformErrors' | 'none';
  /** Errors after `@level === 'error'` filtering (mirrors `extract_tf_errors.py`). */
  entries: TerraformErrorEntry[];
  /** Original payload, for users who want to download the unprocessed log. */
  raw: unknown;
}

/**
 * Parses a payload that may be a JSON array, a `{ errors: [...] }` object,
 * or NDJSON (newline-delimited JSON, common for raw terraform stdout).
 * Returns the array of log entries, or `null` if the shape is unknown.
 */
function parseTerraformPayload(text: string): unknown {
  const trimmed = text.trim();
  if (!trimmed) return [];
  try {
    return JSON.parse(trimmed);
  } catch {
    const lines = trimmed.split('\n').map((l) => l.trim()).filter(Boolean);
    const out: TerraformErrorEntry[] = [];
    for (const line of lines) {
      try {
        const obj = JSON.parse(line);
        if (obj && typeof obj === 'object') out.push(obj as TerraformErrorEntry);
      } catch {
        /* skip non-JSON lines */
      }
    }
    return out;
  }
}

/**
 * Fetches terraform error entries by trying available presigned URLs in order:
 *
 *  1. `terraformErrors.presignedUrl`  — backend-cached pre-extracted errors
 *     `{ errors: [...], totalErrors, ... }` — equivalent to `extract_tf_errors.py` output.
 *  2. `presignedUrls.terraformLogs`   — raw `2nd-import-stdout.json` (may be NDJSON),
 *     equivalent to the raw input that `extract_tf_errors.py` processes.
 *
 * No status checks — uses whatever URL is present.
 */
export async function fetchTerraformErrors(
  transactionId: string,
  region: string,
  jobDetails?: JobDetailsResponse,
): Promise<TerraformErrorsFetchResult> {
  const details =
    jobDetails ??
    (await jobService.getJobDetails(transactionId, region).catch((err) => {
      console.warn('[fetchTerraformErrors] getJobDetails failed', err);
      return undefined;
    }));

  if (!details) {
    console.warn('[fetchTerraformErrors] no job details available');
    return { source: 'none', entries: [], raw: null };
  }

  const cachedUrl = details.terraformErrors?.presignedUrl;
  const rawUrl    = details.presignedUrls?.terraformLogs;

  console.log('[fetchTerraformErrors] urls →', {
    cachedUrl: cachedUrl ? 'present' : 'absent',
    rawUrl:    rawUrl    ? 'present' : 'absent',
    status:    details.terraformErrors?.status,
  });

  // ── 1. Cached (pre-extracted) errors ────────────────────────────────────────
  if (cachedUrl) {
    try {
      const resp = await fetch(cachedUrl);
      if (resp.ok) {
        const data = (await resp.json()) as TerraformErrorsResponse;
        const entries = extractTerraformErrorEntries(data);
        console.log(`[fetchTerraformErrors] cached → ${entries.length} entries`);
        if (entries.length > 0) {
          return { source: 'terraformErrors', entries, raw: data };
        }
      } else {
        console.warn('[fetchTerraformErrors] cached not OK', resp.status);
      }
    } catch (err) {
      console.warn('[fetchTerraformErrors] cached threw', err);
    }
  }

  // ── 2. Raw stdout log ────────────────────────────────────────────────────────
  if (rawUrl) {
    try {
      const resp = await fetch(rawUrl);
      if (resp.ok) {
        const text = await resp.text();
        const data = parseTerraformPayload(text);
        const entries = extractTerraformErrorEntries(data);
        console.log(`[fetchTerraformErrors] raw stdout → ${entries.length} entries`);
        return { source: 'terraformLogs', entries, raw: data };
      } else {
        console.warn('[fetchTerraformErrors] raw not OK', resp.status);
      }
    } catch (err) {
      console.warn('[fetchTerraformErrors] raw threw', err);
    }
  }

  console.warn('[fetchTerraformErrors] no usable source found');
  return { source: 'none', entries: [], raw: null };
}

export interface FlowErrorsFetchResult {
  /** Aggregated minified output (mirrors `aggregate_flow_errors.py`). */
  minified: MinifiedFlowErrors;
  /** Source presigned URL (`publishedFlows.errors`, i.e. the
   * `firstPublishFlowsFromSourceErrors.zip`). */
  sourceUrl?: string;
}

/**
 * Downloads `presignedUrls.publishedFlows.errors` (the
 * `firstPublishFlowsFromSourceErrors` zip) and runs the
 * `aggregate_flow_errors.py` logic over every `*-publish-results-N.json`
 * entry inside.
 */
export async function fetchFlowErrors(
  transactionId: string,
  region: string,
  jobDetails?: JobDetailsResponse,
): Promise<FlowErrorsFetchResult> {
  const details =
    jobDetails ??
    (await jobService.getJobDetails(transactionId, region).catch((err) => {
      console.warn('[fetchFlowErrors] getJobDetails failed', err);
      return undefined;
    }));
  const url = details?.presignedUrls?.publishedFlows?.errors;
  if (!url) {
    console.log('[fetchFlowErrors] no publishedFlows.errors URL available');
    return { minified: {} };
  }

  try {
    const resp = await fetch(url);
    if (!resp.ok) {
      console.warn('[fetchFlowErrors] publishedFlows.errors not OK', resp.status);
      return { minified: {}, sourceUrl: url };
    }
    const blob = await resp.blob();
    const minified = await aggregateFlowErrorsFromZip(blob);
    console.log(
      `[fetchFlowErrors] aggregated ${Object.keys(minified).length} module(s)`,
    );
    return { minified, sourceUrl: url };
  } catch (err) {
    console.warn('[fetchFlowErrors] publishedFlows.errors threw', err);
    return { minified: {}, sourceUrl: url };
  }
}

// ───────────────────────────────────────────────────────────────────────────────
// Prompt construction
// ───────────────────────────────────────────────────────────────────────────────

interface JobSummaryPromptInputs {
  txn: TransactionDetail;
  terraformGroups?: MinifiedTerraformGroup[];
  flowErrors?: MinifiedFlowErrors;
}

function flowErrorTotals(flows: MinifiedFlowErrors): { modules: number; flows: number } {
  let totalFlows = 0;
  for (const entries of Object.values(flows)) totalFlows += entries.length;
  return { modules: Object.keys(flows).length, flows: totalFlows };
}

function terraformTotals(groups: MinifiedTerraformGroup[]): { types: number; total: number } {
  return {
    types: groups.length,
    total: groups.reduce((s, g) => s + g.count, 0),
  };
}

export function buildJobSummaryPrompt({
  txn,
  terraformGroups,
  flowErrors,
}: JobSummaryPromptInputs): string {
  const source = txn.exportOrgs?.sourceOrg;
  const dest = txn.exportOrgs?.destinationOrg;
  const parity = txn.totalResourcesOnSourceOrg
    ? `${txn.totalMatchingResourcesOnDestOrg ?? 0}/${txn.totalResourcesOnSourceOrg}`
    : 'N/A';

  const lines: string[] = [
    'Please provide a comprehensive summary of this BCP Wizard sync job.',
    'Note: DO NOT SHOW FAILED OR SUCCESS STATUS IN THE SUMMARY, ONLY SHOW THE PARITY STATUS no completion percentage.',
    '',
    '**Job Details:**',
    `- Transaction ID: ${txn.transactionId ?? 'N/A'}`,
    `- Job ID: ${txn.jobId ?? 'N/A'}`,
    `- Action: ${txn.action ?? 'N/A'}`,
    `- Status: ${txn.status ?? 'N/A'}`,
    `- Type: ${txn.type ?? 'N/A'}`,
    `- Host: ${txn.host ?? 'N/A'}`,
    `- Started: ${txn.dateStarted ?? 'N/A'}`,
    `- Finished: ${txn.dateFinished ?? 'N/A'}`,
    `- Initiated By: ${txn.initiatedBy ?? 'N/A'}`,
    `- Completion: ${txn.completionPercentage ?? txn.completion ?? 'N/A'}%`,
    '',
    '**Organizations:**',
    `- Source: ${source?.name ?? 'N/A'} (Region: ${source?.region ?? 'N/A'}, ID: ${source?.id ?? source?.orgId ?? 'N/A'})`,
    `- Destination: ${dest?.name ?? 'N/A'} (Region: ${dest?.region ?? 'N/A'}, ID: ${dest?.id ?? dest?.orgId ?? 'N/A'})`,
    '',
    `**Sync Parity:** ${parity}`,
  ];

  const successSteps = txn.successfullSteps ?? txn.successfulSteps;
  if (successSteps?.length) lines.push(`**Successful Steps:** ${successSteps.join(', ')}`);
  if (txn.failedSteps?.length) lines.push(`**Failed Steps:** ${txn.failedSteps.join(', ')}`);

  const otherErrorLines: string[] = [];
  if (txn.error) otherErrorLines.push(`- Job error: ${txn.error}`);
  if (txn.importFlowsError) otherErrorLines.push(`- Import flows error: ${txn.importFlowsError}`);
  if (txn.featureError) otherErrorLines.push('- Feature error: yes');

  if (otherErrorLines.length) {
    lines.push('', '**Other Reported Errors:**', ...otherErrorLines);
  }

  const hasTerraform = !!terraformGroups && terraformGroups.length > 0;
  const hasFlows = !!flowErrors && Object.keys(flowErrors).length > 0;

  if (hasTerraform) {
    const totals = terraformTotals(terraformGroups!);
    lines.push(
      '',
      `**Terraform Errors (minified by resource type — ${totals.types} type(s), ${totals.total} error(s)):**`,
      'Generated by the equivalent of `extract_tf_errors.py` + `minify_tf_errors.py`. Each entry is `{ resourceType, count, resources: [{ message, detail, resourceName }] }` — the full per-resource list grouped by Terraform resource type.',
      '```json',
      JSON.stringify(terraformGroups, null, 2),
      '```',
    );
  }

  if (hasFlows) {
    const totals = flowErrorTotals(flowErrors!);
    lines.push(
      '',
      `**Flow Publish Errors (aggregated from Archy publish-results — ${totals.flows} flow(s) across ${totals.modules} module(s)):**`,
      'Generated by the equivalent of `aggregate_flow_errors.py`. Shape: `{ <module>: [{ flowName, errors: [...] }] }` — meaningful errors per flow (noise filtered, deduped, first-seen order).',
      '```json',
      JSON.stringify(flowErrors, null, 2),
      '```',
    );
  }

  lines.push(
    '',
    'Provide your response in the following clearly-separated sections. Use the exact `## Heading` text shown so they render as distinct sections — do NOT merge them.',
    '',
    '## Job Summary',
    '1. Brief overview of what this job did (action type, source → destination org sync).',
    '2. The sync parity status (do NOT mention completion percentage or pass/fail).',
    '3. If failed steps or errors exist, what likely went wrong and high-level troubleshooting steps.',
    '4. Notable observations or recommendations.',
    '5. Cite any relevant KB articles for the failure mode.',
  );

  if (hasTerraform) {
    lines.push(
      '',
      '## Terraform Error Summary',
      '_Output this section only if Terraform errors are present above._',
      '1. For each unique error pattern in the JSON, explain the root cause in plain language.',
      '2. Give specific resolution steps per pattern (not per individual resource).',
      '3. Call out relationships between patterns (e.g. cascading failures).',
      '4. Cite relevant KB articles.',
      'Do NOT mix terraform analysis into the Job Summary.',
    );
  }

  if (hasFlows) {
    lines.push(
      '',
      '## Flow Error Summary',
      '_Output this section only if Flow Publish errors are present above._',
      '1. For each module, summarise the dominant failure pattern(s).',
      '2. Translate Archy / Architect-Scripting jargon into plain English.',
      '3. Give resolution steps per pattern (e.g. "force-unlock locked flows", "register missing data action", "add TTS voice").',
      '4. Cite relevant KB articles.',
      'Do NOT mix flow analysis into the Job Summary or Terraform Error Summary.',
    );
  }

  if (otherErrorLines.length) {
    lines.push(
      '',
      '## Other Issues',
      '_Output this section only if "Other Reported Errors" are listed above and they are not already covered by Terraform / Flow analysis._',
      'Briefly explain each other reported error and suggest next steps.',
    );
  }

  return lines.join('\n');
}

export interface JobSummaryFetchResult {
  prompt: string;
  txn: TransactionDetail;
  /** Filtered terraform error entries (matches `2nd-import-errors-extracted.json`). */
  terraformExtracted: TerraformErrorEntry[];
  /** Minified terraform groups (matches `2nd-import-errors-minified.json`). */
  terraformMinified: MinifiedTerraformGroup[];
  /** Minified flow errors (matches `flow-errors-minified.json`). */
  flowErrors: MinifiedFlowErrors;
  /** Original raw terraform payload — handy for "Download raw stdout". */
  terraformRaw: unknown;
  /** Source URL the terraform raw came from. */
  terraformSource: TerraformErrorsFetchResult['source'];
}

export async function fetchJobSummaryPrompt(
  transactionId: string,
): Promise<JobSummaryFetchResult> {
  const txn = await transactionService.getTransactionById(transactionId);
  const region = txn.deploymentRegion ?? 'us-west-2';

  const jobDetails = await jobService
    .getJobDetails(transactionId, region)
    .catch(() => undefined);

  const [tfResult, flowResult] = await Promise.all([
    fetchTerraformErrors(transactionId, region, jobDetails ?? undefined),
    fetchFlowErrors(transactionId, region, jobDetails ?? undefined),
  ]);

  const terraformMinified = minifyTerraformErrorsByResourceType(tfResult.entries);
  const flowErrors = flowResult.minified;

  const prompt = buildJobSummaryPrompt({
    txn,
    terraformGroups: terraformMinified.length > 0 ? terraformMinified : undefined,
    flowErrors: Object.keys(flowErrors).length > 0 ? flowErrors : undefined,
  });

  return {
    prompt,
    txn,
    terraformExtracted: tfResult.entries,
    terraformMinified,
    flowErrors,
    terraformRaw: tfResult.raw,
    terraformSource: tfResult.source,
  };
}
